import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DatabaseBackup, Loader2, Download, RotateCcw, Trash2, ShieldCheck, HardDrive, AlertTriangle } from "lucide-react";

const fmtBytes = (n) => {
  n = Number(n) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const TYPE_LABEL = { auto: "Automatico", manual: "Manuale", "pre-restore": "Pre-ripristino" };

export const BackupManager = () => {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [targets, setTargets] = useState([]);
  const [target, setTarget] = useState("");
  const [backups, setBackups] = useState([]);
  const [retention, setRetention] = useState(7);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoreT, setRestoreT] = useState(null);
  const [deleteT, setDeleteT] = useState(null);
  const [busy, setBusy] = useState("");
  const [selectedId, setSelectedId] = useState("");

  const completed = backups.filter((b) => b.status === "completed");
  const running = backups.find((b) => b.status === "running");
  const selected = completed.find((b) => b.id === selectedId) || null;
  useEffect(() => {
    if (completed.length && !completed.some((b) => b.id === selectedId)) {
      setSelectedId(completed[0].id);
    }
  }, [completed, selectedId]);

  const qs = target ? `?user_id=${encodeURIComponent(target)}` : "";

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    return api.get(`/backups${target ? `?user_id=${encodeURIComponent(target)}` : ""}`)
      .then((r) => { setBackups(r.data.backups || []); setRetention(r.data.retention || 7); })
      .catch(() => { if (!silent) toast.error("Impossibile caricare i backup"); })
      .finally(() => { if (!silent) setLoading(false); });
  }, [target]);

  useEffect(() => {
    if (isSuper) api.get("/admin/backup-users").then((r) => setTargets(r.data.users || [])).catch(() => {});
  }, [isSuper]);
  useEffect(() => { load(); }, [load]);

  // Polling: se c'è un backup in corso, aggiorna il progresso ogni 1.5s (silenzioso)
  const hasRunning = backups.some((b) => b.status === "running");
  useEffect(() => {
    if (!hasRunning) return;
    const t = setInterval(() => load(true), 1500);
    return () => clearInterval(t);
  }, [hasRunning, load]);

  const createBackup = async () => {
    setCreating(true);
    try { await api.post(`/backups${qs}`); toast.success("Backup avviato — continua in background"); await load(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Backup non riuscito"); }
    finally { setCreating(false); }
  };

  const doDownload = async (b) => {
    setBusy(b.id);
    try {
      const r = await api.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `sedio-backup-${(b.created_at || "").slice(0, 10)}-${b.id.slice(0, 8)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
    finally { setBusy(""); }
  };

  const doRestore = async () => {
    if (!restoreT) return;
    setBusy(restoreT.id);
    try {
      const r = await api.post(`/backups/${restoreT.id}/restore`);
      toast.success(`Ripristino completato (${r.data.files_restored} file)`);
      setRestoreT(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ripristino non riuscito"); }
    finally { setBusy(""); }
  };

  const doDelete = async () => {
    if (!deleteT) return;
    setBusy(deleteT.id);
    try { await api.delete(`/backups/${deleteT.id}`); toast.success("Backup eliminato"); setDeleteT(null); load(); }
    catch { toast.error("Eliminazione non riuscita"); }
    finally { setBusy(""); }
  };

  return (
    <div className="bg-card border border-border rounded-md p-6" data-testid="backup-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <DatabaseBackup className="h-4 w-4 text-[#007BFF]" />
          <h3 className="font-head text-lg font-semibold">Backup e ripristino</h3>
        </div>
        <Button data-testid="create-backup-btn" onClick={createBackup} disabled={creating || hasRunning}
          className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md h-9">
          {creating || hasRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <DatabaseBackup className="h-4 w-4 mr-2" />}
          {hasRunning ? "Backup in corso…" : "Crea backup ora"}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Copia dei tuoi dati (condomìni, pratiche, documenti, portali e impostazioni) su archivio sicuro.
        Per renderlo veloce e leggero, le <strong>email e i loro allegati non vengono inclusi</strong> (si riscaricano
        automaticamente dalle caselle IMAP/Gmail). Backup <strong>automatico ogni notte</strong>, conservati gli ultimi <strong>{retention}</strong>.
        Il ripristino sovrascrive i dati attuali (viene comunque creato uno snapshot di sicurezza prima) e non tocca le email.
      </p>

      {(() => {
        // Mostra un avviso SOLO per l'ultimo backup e SOLO se è andato male.
        // Se l'ultimo backup è andato a buon fine, non si mostra alcun log.
        const last = backups.find((b) => b.status !== "running");
        if (!last) return null;
        const isFailed = last.status === "failed";
        const isPartial = last.status === "completed" && last.files_failed > 0;
        if (!isFailed && !isPartial) return null;
        return (
          <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900/50 p-3 flex items-start gap-2.5" data-testid="backup-error-alert">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-[13px] text-amber-800 dark:text-amber-200">
              <div className="font-semibold">
                {isFailed
                  ? "L'ultimo backup non è riuscito"
                  : "L'ultimo backup è incompleto"}
              </div>
              <div className="mt-0.5">
                {isFailed
                  ? <>Backup del <strong>{fmtDate(last.created_at)}</strong>{last.error ? `: ${last.error}` : "."}</>
                  : <>Backup del <strong>{fmtDate(last.created_at)}</strong>: <strong>{last.files_failed}</strong> file non copiati.</>}
              </div>
              <div className="mt-1 text-[12px] opacity-90">Ti consigliamo di rieseguire il backup con "Crea backup ora".</div>
            </div>
          </div>
        );
      })()}

      {isSuper && (
        <div className="mb-4" data-testid="backup-target-select">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Amministratore</label>
          <select value={target} onChange={(e) => setTarget(e.target.value)}
            className="mt-1 block w-full max-w-md rounded-md border border-border bg-background px-3 py-2 text-sm">
            <option value="">Il mio account ({user?.email})</option>
            {targets.filter((t) => t.user_id !== user?.user_id).map((t) => (
              <option key={t.user_id} value={t.user_id}>{t.email} ({t.backups} backup)</option>
            ))}
          </select>
        </div>
      )}

      <div className="border border-border rounded-lg p-4" data-testid="backup-list">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : running ? (
          <div className="py-2" data-testid="backup-running">
            <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
              <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" />{running.phase || "Backup in corso"}</span>
              <span>{running.percent || 0}%</span>
            </div>
            <div className="h-2 rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-[#007BFF] rounded-full transition-all duration-500" style={{ width: `${running.percent || 0}%` }} />
            </div>
          </div>
        ) : completed.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-8">Nessun backup disponibile. Creane uno con "Crea backup ora".</div>
        ) : (
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1.5">
              <RotateCcw className="h-3.5 w-3.5" />Ripristina un backup
            </label>
            <div className="mt-2 flex flex-col sm:flex-row gap-2">
              <select data-testid="backup-select" value={selectedId} onChange={(e) => setSelectedId(e.target.value)}
                className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm">
                {completed.map((b) => (
                  <option key={b.id} value={b.id}>
                    {fmtDate(b.created_at)} · {TYPE_LABEL[b.type] || b.type} · {fmtBytes(b.size_bytes)}
                    {b.files_failed > 0 ? ` · ${b.files_failed} file mancanti` : ""}
                  </option>
                ))}
              </select>
              <Button data-testid="restore-selected-btn" onClick={() => selected && setRestoreT(selected)} disabled={!selected}
                className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md h-9 shrink-0">
                <RotateCcw className="h-4 w-4 mr-2" />Ripristina
              </Button>
            </div>
            {selected && (
              <div className="mt-3 flex items-center gap-4 text-[11px] text-muted-foreground flex-wrap">
                <span><HardDrive className="h-3 w-3 inline mr-1" />{fmtBytes(selected.size_bytes)}</span>
                <span>{selected.db_docs_count} record</span>
                <span>{selected.files_count} file</span>
                <button onClick={() => doDownload(selected)} disabled={busy === selected.id} data-testid="backup-download-selected"
                  className="inline-flex items-center gap-1 font-medium text-muted-foreground hover:text-foreground disabled:opacity-50">
                  {busy === selected.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}Scarica
                </button>
                <button onClick={() => setDeleteT(selected)} data-testid="backup-delete-selected"
                  className="inline-flex items-center gap-1 font-medium text-red-600 hover:underline">
                  <Trash2 className="h-3.5 w-3.5" />Elimina
                </button>
              </div>
            )}

            <div className="mt-4 border-t border-border pt-3" data-testid="backup-size-list">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">Backup disponibili</div>
              <div className="max-h-[11rem] overflow-y-auto scroll-thin pr-1" data-testid="backup-size-scroll">
                <ul className="divide-y divide-border/60">
                  {completed.map((b) => (
                    <li key={b.id} className="flex items-center justify-between py-1.5 text-sm" data-testid={`backup-item-${b.id}`}>
                      <span className="text-foreground truncate">{fmtDate(b.created_at)} · {TYPE_LABEL[b.type] || b.type}</span>
                      <span className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground shrink-0" data-testid={`backup-size-${b.id}`}>
                        <HardDrive className="h-3 w-3" />{fmtBytes(b.size_bytes)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Ripristino */}
      <AlertDialog open={!!restoreT} onOpenChange={(o) => { if (!o) setRestoreT(null); }}>
        <AlertDialogContent data-testid="backup-restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><RotateCcw className="h-5 w-5 text-[#007BFF]" />Ripristina backup</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>Stai per ripristinare il backup del <strong>{fmtDate(restoreT?.created_at)}</strong>.</p>
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900/50 p-3 text-amber-800 dark:text-amber-200 flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Tutti i dati attuali verranno <strong>sostituiti</strong> con quelli del backup. Prima del ripristino viene creato automaticamente uno snapshot di sicurezza, così potrai tornare indietro.</span>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === restoreT?.id}>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="backup-restore-confirm" disabled={busy === restoreT?.id}
              onClick={(e) => { e.preventDefault(); doRestore(); }} className="bg-[#007BFF] hover:bg-[#0069D9]">
              {busy === restoreT?.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ripristina ora"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Eliminazione */}
      <AlertDialog open={!!deleteT} onOpenChange={(o) => { if (!o) setDeleteT(null); }}>
        <AlertDialogContent data-testid="backup-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Elimina backup</AlertDialogTitle>
            <AlertDialogDescription>
              Eliminare definitivamente il backup del {fmtDate(deleteT?.created_at)}? L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === deleteT?.id}>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="backup-delete-confirm" disabled={busy === deleteT?.id}
              onClick={(e) => { e.preventDefault(); doDelete(); }} className="bg-red-600 hover:bg-red-700">
              {busy === deleteT?.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
